import React from 'react';
import { useAuthStore } from '../../store/authStore';
import { Users, UserPlus, Mail, Trash2, Shield } from 'lucide-react';

export const TeamManagement = () => {
  const { team, user, addTeamMember, removeTeamMember } = useAuthStore();
  const [showInviteForm, setShowInviteForm] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<'admin' | 'member'>('member');

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    addTeamMember({
      email: inviteEmail,
      name: inviteEmail.split('@')[0],
      role: inviteRole,
    });
    setInviteEmail('');
    setShowInviteForm(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Team Management</h2>
            <p className="text-sm text-gray-500">Manage your team members and their roles</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Team Members List */}
          <div className="space-y-4">
            {team?.members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1E293B] flex items-center justify-center text-white">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{member.name}</h3>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    member.role === 'owner'
                      ? 'bg-purple-50 text-purple-700'
                      : member.role === 'admin'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                  </span>
                  
                  {user?.role === 'owner' && member.id !== user.id && (
                    <button
                      onClick={() => removeTeamMember(member.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Invite Form */}
          {(user?.role === 'owner' || user?.role === 'admin') && (
            <div>
              {showInviteForm ? (
                <form onSubmit={handleInvite} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="input-field"
                      placeholder="team@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                      className="input-field"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="flex gap-2">
                    <button type="submit" className="btn">
                      Send Invite
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowInviteForm(false)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="btn flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite Team Member
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};